import {
  Input,
  Modal,
  NewButton,
  NewButtonVariant,
} from "@visiblethread/react-components";
import clsx from "clsx";
import ChoiceGroup, { choiceGroupLayouts } from "components/ChoiceGroup";
import Flex, { GAP_CLASS } from "components/Flex";
import { ALIGN_CLASS, DIRECTION_CLASS } from "components/Flex/Flex";
import { modalTypes } from "constants/types";
import { Plus, X } from "lucide-react";
import { func } from "prop-types";
import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { hideModal } from "slices/app/actions/modals";

import classes from "./KanbanAddStageGateQuestion.module.scss";

const QUESTION_TYPES = [
  { value: "YES_NO", label: "Yes / No" },
  { value: "MULTIPLE_CHOICE", label: "Multiple Choice" },
  { value: "FREE_TEXT", label: "Free Text" },
];

const MIN_OPTIONS = 2;

const KanbanAddStageGateQuestion = ({ onAdd }) => {
  const dispatch = useDispatch();
  const [type, setType] = useState(QUESTION_TYPES[0].value);
  const [text, setText] = useState("");
  const [options, setOptions] = useState(["", ""]);

  const trimmedOptions = useMemo(
    () => options.map((option) => option.trim()).filter(Boolean),
    [options]
  );

  const isValid = useMemo(() => {
    if (!text.trim()) {
      return false;
    }
    if (type === "MULTIPLE_CHOICE") {
      return trimmedOptions.length >= MIN_OPTIONS;
    }
    return true;
  }, [text, type, trimmedOptions]);

  const onDismiss = () => {
    dispatch(hideModal(modalTypes.KANBAN_ADD_STAGE_GATE_QUESTION));
  };

  const onAddOption = () => setOptions((prev) => [...prev, ""]);

  const onRemoveOption = (index) =>
    setOptions((prev) => prev.filter((_, i) => i !== index));

  const onChangeOption = (index, value) =>
    setOptions((prev) =>
      prev.map((option, i) => (i === index ? value : option))
    );

  const onClickConfirm = () => {
    const question = { type, text: text.trim() };
    if (type === "MULTIPLE_CHOICE") {
      question.options = trimmedOptions;
    }
    dispatch(hideModal(modalTypes.KANBAN_ADD_STAGE_GATE_QUESTION));
    onAdd(question);
  };

  return (
    <Modal
      data-testid="kanban-add-stage-gate-question"
      className={clsx("fade-in", classes["kanban_add_stage_gate_question"])}
      show
      onDismiss={onDismiss}
      backdrop
      centered
    >
      <Modal.Header>Add Question</Modal.Header>

      <Modal.Body>
        <Flex
          direction={DIRECTION_CLASS.column}
          alignItems={ALIGN_CLASS.stretch}
          gap={GAP_CLASS.GAP_400}
        >
          <ChoiceGroup
            layout={choiceGroupLayouts.column}
            question={
              <span className={classes["kanban_add_stage_gate_question__label"]}>
                Question Type
              </span>
            }
            options={QUESTION_TYPES}
            value={type}
            onChange={setType}
            fullWidthOptions
          />

          <Flex
            direction={DIRECTION_CLASS.column}
            alignItems={ALIGN_CLASS.stretch}
            gap={GAP_CLASS.GAP_100}
          >
            <label
              htmlFor="add-question-text"
              className={classes["kanban_add_stage_gate_question__label"]}
            >
              Question
            </label>
            <Input
              id="add-question-text"
              placeholder="e.g. Has the bid board approved pursuit?"
              value={text}
              onChange={setText}
            />
          </Flex>

          {type === "MULTIPLE_CHOICE" && (
            <Flex
              direction={DIRECTION_CLASS.column}
              alignItems={ALIGN_CLASS.stretch}
              gap={GAP_CLASS.GAP_200}
            >
              <span
                className={classes["kanban_add_stage_gate_question__label"]}
              >
                Choices
              </span>
              {options.map((option, index) => (
                <Flex
                  key={index}
                  alignItems={ALIGN_CLASS.center}
                  gap={GAP_CLASS.GAP_200}
                  className={
                    classes["kanban_add_stage_gate_question__option_row"]
                  }
                >
                  <Input
                    id={`add-question-option-${index}`}
                    aria-label={`Choice ${index + 1}`}
                    placeholder={`Choice ${index + 1}`}
                    value={option}
                    onChange={(value) => onChangeOption(index, value)}
                  />
                  {options.length > MIN_OPTIONS && (
                    <NewButton
                      variant={NewButtonVariant.l3}
                      aria-label={`Remove Choice ${index + 1}`}
                      onClick={() => onRemoveOption(index)}
                    >
                      <X size={16} />
                    </NewButton>
                  )}
                </Flex>
              ))}
              <NewButton
                variant={NewButtonVariant.l2}
                className={
                  classes["kanban_add_stage_gate_question__add_choice"]
                }
                onClick={onAddOption}
              >
                <Plus size={16} />
                Add Choice
              </NewButton>
            </Flex>
          )}
        </Flex>
      </Modal.Body>

      <Modal.Footer>
        <NewButton variant={NewButtonVariant.l2} onClick={onDismiss}>
          Cancel
        </NewButton>
        <NewButton
          variant={NewButtonVariant.l1}
          disabled={!isValid}
          onClick={onClickConfirm}
        >
          Add Question
        </NewButton>
      </Modal.Footer>
    </Modal>
  );
};

KanbanAddStageGateQuestion.propTypes = { onAdd: func.isRequired };

export default KanbanAddStageGateQuestion;
